// src/components/Entregas/ProductosOrdenServicios.jsx
import React, { useCallback, useMemo, useEffect, useState } from "react";

import { useSelector } from "react-redux";
import {
  DatePicker,
  ConfigProvider,
  Table,
  Spin,
  Switch,
  Popconfirm,
  Badge,
  Button,
  Tooltip,
  Tag,
  Input,
  InputNumber,
  Select,
  Space,
  message,
  Col,
  Row,
  Card,
} from "antd";
import {
  SaveOutlined,
  DeleteOutlined,
  BarcodeOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { getUnidadesDeMedida } from "../../services/financiero/plandecompras/unidadesDeMedida";
import {
  registrarProductoOrden,
  eliminarProductoOrden,
  actualizarProductoOrden,
} from "../../services/entregas/servicesOrdenesCompra";
import { getOrdenesServiciosDetalle } from "../../services/entregas/servicesOrdenesCompra";

import "./ordenesServicios.css";
import ProductosPOSeriales from "./ProductosPOSeriales";

import dayjs from "dayjs";
import "dayjs/locale/es";
import esES from "antd/locale/es_ES";

dayjs.locale("es");

const { Option } = Select;

// Opciones de IVA
const IVA_SELECT_OPTIONS = [
  { label: "No causado", value: "NC" }, // guardaremos null
  { label: "0% (Exento)", value: "0" },
  { label: "5%", value: "5" },
  { label: "19%", value: "19" },
];

const toNum = (x) =>
  x === null || x === undefined || x === "" ? 0 : Number(x);
const fmtCOP = (n) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const totalSinIVA = (r) => toNum(r.cantidad) * toNum(r.valor_unitario);
const valorIVAcalc = (r) => {
  const tasa =
    r.valorIVA === null || r.valorIVA === undefined || r.valorIVA === ""
      ? null
      : Number(r.valorIVA);
  if (tasa === null) return 0; // No causado
  // tasa 0 => exento => también 0
  return totalSinIVA(r) * (tasa / 100);
};
const totalConIVA = (r) => totalSinIVA(r) + valorIVAcalc(r);

const ProductosOrdenServicios = ({
  oidPo,
  isEditing,
  onCambiosGuardado,
  parsedProducts = [],
  orden,
}) => {
  const [unidades, setUnidades] = useState([]);
  const [detalles, setDetalles] = useState([]);
  const [loading, setLoading] = useState(false);

  const [serialModalVisible, setSerialModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [loadingUnidades, setLoadingUnidades] = useState(false);
  const [valorTotalOrden, setValorTotalOrden] = useState(0);
  const [valorTotalOrdenConIVA, setValorTotalOrdenConIVA] = useState(0);

  const userBodecom = useSelector((state) => state.auth.user);
  const [showDetalle, setShowDetalle] = React.useState(false); // oculto por defecto

  const [originalData, setOriginalData] = useState([]);


  const calcularValorTotal = useCallback((items) => {
    const total = items
      .filter((item) => item.guardado && item.cantidad && item.valor_unitario)
      .reduce((acc, item) => acc + item.cantidad * item.valor_unitario, 0);
    setValorTotalOrden(total);
  }, []);

  const calcularValorTotalConIVA = useCallback((items) => {
    const total = items
      .filter((item) => item.guardado && item.cantidad && item.valor_unitario)
      .reduce((acc, item) => {
        const cantidad = Number(item.cantidad) || 0;
        const valorUnitario = Number(item.valor_unitario) || 0;
        const ivaPorcentaje = Number(item.valorIVA) || 0;

        // Calcular valor con IVA
        const valorUnitarioConIVA =
          valorUnitario + (valorUnitario * ivaPorcentaje) / 100;

        return acc + cantidad * valorUnitarioConIVA;
      }, 0);

    setValorTotalOrdenConIVA(total);
  }, []);

  useEffect(() => {
    if (!isEditing) {
      // Modo “nueva PO” o manual: arrancamos con lista vacía
      setDetalles([]);
      setValorTotalOrden(0); // opcional: también resetea el total
      setOriginalData([]);
      return; // salimos aquí
    }

    if (isEditing && oidPo) {
      const fetchDetalles = async () => {
        try {
          setLoading(true); // Activamos el spinner
          const resp = await getOrdenesServiciosDetalle(oidPo);
          if (
            resp.status === 200 &&
            Array.isArray(resp.data) &&
            resp.data.length > 0
          ) {
            const detalles = resp.data[0].detalles.map((item, idx) => ({
              ...item,
              key: item.oid, // usa oid como key única
              guardado: true, // marca como ya guardado
              oid: item.oid,
              oidProducto: item.oid,
              oidInsertado: item.oid,
              unidadmedida: item.oidUnidad,
              valorIVA: item.valorIVA,
              cantidad: Number(item.cantidad) ?? 0,
              cantidadDB: Number(item.cantidad) ?? 0,
              fechaEnvio: item.fechaEnvio,
            }));
            setDetalles(detalles);
            calcularValorTotal(detalles); // 🔄 actualiza el total al cargar
            calcularValorTotalConIVA(detalles);
            setOriginalData(detalles);

          } else {
            message.warning(
              resp.message || "No se encontraron detalles para esta orden"
            );
          }
        } catch (error) {
          console.error("Error al obtener detalles:", error);
          message.error("Error al cargar detalles de productos");
        } finally {
          setLoading(false);
        }
      };

      fetchDetalles();
    }
  }, [oidPo, isEditing, calcularValorTotal, calcularValorTotalConIVA]);

  // 2) en montaje, cargo las unidades
  useEffect(() => {
    const fetchUnidades = async () => {
      try {
        setLoadingUnidades(true);
        const resp = await getUnidadesDeMedida();

        // asumo que resp.data es un array con { oid, Unidad_medida }
        if (Array.isArray(resp.data)) {
          setUnidades(resp.data);
        } else {
          throw new Error("Formato inesperado de unidades");
        }
      } catch (err) {
        console.error(err);
        // aquí podrías mostrar un mensaje de error
      } finally {
        setLoadingUnidades(false);
      }
    };
    fetchUnidades();
  }, []);

  useEffect(() => {
    const haySinGuardar = detalles.some((item) => !item.guardado);
    onCambiosGuardado?.(haySinGuardar, valorTotalOrden);
  }, [detalles, valorTotalOrden, onCambiosGuardado]);

  function limpiarValor(raw) {
    // 1. Asegurémonos de que raw es string y recortamos espacios
    const str = String(raw ?? "").trim();
    // 2. Eliminamos todo lo que no sea dígito, coma, punto o signo menos
    let cleaned = str.replace(/[^\d,.-]/g, "");
    // 3. Quitamos todas las comas (eran separadores de miles)
    cleaned = cleaned.replace(/,/g, "");
    // 4. Parseamos a número
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  }

  function limpiarProducto(raw) {
    const str = String(raw ?? "");
    return (
      str
        // elimina la secuencia literal backslash + n
        .replace(/\\n/g, " ")
        // elimina saltos de línea reales
        .replace(/\r?\n/g, " ")
        // reduce múltiples espacios a uno solo
        .replace(/\s+/g, " ")
        .trim()
    );
  }

  useEffect(() => {
    // sólo si vienen productos parseados y NO estamos en modo edición

    if (!isEditing && parsedProducts.length > 0) {
      const inicial = parsedProducts.map((p, idx) => ({
        key: `parsed-${idx}`, // clave única
        producto: limpiarProducto(p.producto), // pon aquí el campo correcto
        unidadmedida: 336, // o como venga tu objeto
        cantidad: p.cantidad,
        valor_unitario: limpiarValor(p.precioCOP),
        guardado: false, // para que el usuario decida guardar
        valorIVA: p.valorIVA,
        fechaEnvio: p.fechaEnvio ?? null,
      }));

      setDetalles(inicial);
      calcularValorTotal(inicial);
      calcularValorTotalConIVA(inicial);
    }
  }, [parsedProducts, isEditing, calcularValorTotal, calcularValorTotalConIVA]);

  const markRowError = useCallback((rowKey, msg) => {
    setDetalles((prev) =>
      prev.map((item) =>
        item.key === rowKey ? { ...item, _error: true, _errorMsg: msg } : item
      )
    );

    // Desplaza a la fila
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-row-key="${rowKey}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, []);

  // 1️⃣ Calculamos los registros pendientes de guardar
  // 1) Memoizamos la lista de pendientes
  const pendientes = useMemo(
    () => detalles.filter((item) => !item.guardado),
    [detalles]
  );

  // 2️⃣ Función para guardar todos los pendientes de una
  // 2) Handler de guardado masivo
  // Actualiza handleGuardarPendientes para recircular correctamente el total
  const handleGuardarPendientes = async () => {
    if (!pendientes.length) return;
    setLoading(true);
    try {
      // Preparamos sólo los campos que espera el backend
      const payload = pendientes.map(
        ({ producto, unidadmedida, cantidad, valor_unitario, fechaEnvio }) => ({
          producto,
          unidadmedida,
          cantidad,
          valor_unitario,
          fechaEnvio,
        })
      );

      // Envío en lote
      const resp = await registrarProductoOrden(payload, oidPo);

      if (resp.status === 200 || resp.status === 201) {
        const oids = resp.data; // array de insertIds en el mismo orden

        // Reconstruimos el array completo de detalles
        const nuevosDetalles = detalles.map((item) => {
          const idx = pendientes.findIndex((p) => p.key === item.key);
          if (idx >= 0) {
            const oidInsertado = oids[idx];
            return {
              ...item,
              guardado: true,
              esNuevo: false,
              oid: oidInsertado,
              oidProducto: oidInsertado,
              oidInsertado,
              key: oidInsertado, // <- cambia key al OID real
            };
          }
          return item;
        });

        // Actualizamos estado y recalculamos el total usando el array actualizado
        setDetalles(nuevosDetalles);
        calcularValorTotal(nuevosDetalles);
        calcularValorTotalConIVA(nuevosDetalles);

        message.success(`Se guardaron ${pendientes.length} productos.`);
      } else {
        message.error(resp.message || "Error guardando productos");
      }
    } catch (err) {
      console.error("Error al guardar pendientes:", err);
      message.error("Error al guardar productos pendientes.");
    } finally {
      setLoading(false);
    }
  };

  // Devuelve SIEMPRE el OID del detalle de la PO (producto en la PO)
  const getProductoOid = (rec) => {
    const cand = rec?.oidProducto ?? rec?.oid ?? rec?.oidInsertado ?? rec?.key;
    const n = Number(cand);
    return Number.isFinite(n) ? n : null;
  };

  const handleAdd = useCallback(() => {
    setDetalles((d) => [
      ...d,
      {
        key: Date.now(),
        producto: null,
        unidadmedida: 336,
        cantidad: null,
        valor_unitario: 0,
        valorIVA: null, // por defecto No causado
        guardado: false, // <- importante
        esNuevo: true, // <- opcional si quieres diferenciar
      },
    ]);
  }, []); // ← deps

  const handleChange = useCallback((value, key, field) => {
    setDetalles((prev) =>
      prev.map((item) =>
        item.key === key
          ? {
            ...item,
            [field]: value,
            _error: false,
            _errorMsg: undefined,
          }
          : item
      )
    );
  }, []);

  const handleGuardar = useCallback(
    async (record) => {
      const { producto, cantidad, valor_unitario, unidadmedida } = record;

      if (
        !producto ||
        !unidadmedida ||
        !cantidad ||
        valor_unitario == null ||
        valor_unitario <= 0
      ) {
        message.warning(
          "Por favor completa todos los campos obligatorios y asegúrate de que el valor unitario sea mayor a cero."
        );
        return;
      }

      try {
        setLoading(true);
        const response = await registrarProductoOrden(record, oidPo);

        if (response.status === 200 || response.status === 201) {
          const oidInsertado = response.data?.[0]; // ✅ forma correcta

          message.success("Producto registrado en la Orden de Compra.");

          setDetalles((prev) => {
            const updated = prev.map((item) =>
              item.key === record.key
                ? {
                  ...item,
                  // datos del form
                  producto: record.producto,
                  unidadmedida: record.unidadmedida,
                  cantidad: record.cantidad,
                  valor_unitario: record.valor_unitario,
                  valorIVA: record.valorIVA ?? null,
                  // flags
                  guardado: true,
                  esNuevo: false,
                  // IDs reales
                  oid: oidInsertado,
                  oidProducto: oidInsertado,
                  oidInsertado,
                  // MUY IMPORTANTE: usa el OID como key desde ahora
                  key: oidInsertado,
                  cantidadDB: Number(record.cantidad) || 0,
                }
                : item
            );
            calcularValorTotal(updated);
            calcularValorTotalConIVA(updated);
            return updated;
          });
        } else {
          message.error(response.message || "Error guardando detalle");
        }
      } catch (err) {
        console.error(err);
        message.error("Error guardando detalle");
      } finally {
        setLoading(false);
      }
    },
    [oidPo, calcularValorTotal, calcularValorTotalConIVA]
  );

  const handleActualizar = useCallback(
    async (record) => {
      const oidProducto = Number(
        record?.oidProducto ?? record?.oid ?? record?.oidInsertado ?? record?.key
      );
      const oidPoCabecera = Number(oidPo);

      if (!Number.isFinite(oidProducto) || oidProducto <= 0) {
        message.error("OID de producto inválido para actualizar.");
        return false; // ❌ falla
      }
      if (!Number.isFinite(oidPoCabecera) || oidPoCabecera <= 0) {
        message.error("OID de cabecera (oidPo) inválido.");
        return false;
      }

      const { producto, cantidad, valor_unitario, unidadmedida } = record;

      if (!producto || !unidadmedida || !cantidad || valor_unitario == null || valor_unitario <= 0) {
        message.warning("Por favor completa todos los campos obligatorios.");
        return false;
      }

      const totalSeriales = Number(record.total_seriales || 0);
      const cantidadNueva = Number(cantidad || 0);

      if (cantidadNueva < totalSeriales) {
        message.warning(
          `No puedes reducir la cantidad a ${cantidadNueva} porque hay ${totalSeriales} serial(es) registrados.`
        );
        return false;
      }

      try {
        setLoading(true);
        const payload = { ...record, oidProducto };
        const response = await actualizarProductoOrden(payload, oidPoCabecera);

        if (response.status === 200 || response.status === 201) {
          const oidDevuelto = Number(response?.data?.[0]);
          const oidResultante = Number.isFinite(oidDevuelto) && oidDevuelto > 0 ? oidDevuelto : oidProducto;

          message.success("Producto actualizado en la Orden de Compra.");

          setDetalles((prev) => {
            const updated = prev.map((item) => {
              if (item.key !== record.key) return item;
              return {
                ...item,
                producto,
                unidadmedida,
                cantidad,
                valor_unitario,
                guardado: true,
                oid: oidResultante,
                oidProducto: oidResultante,
                oidInsertado: oidResultante,
                cantidadDB: Number(cantidad),
              };
            });
            calcularValorTotal(updated);
            calcularValorTotalConIVA(updated);
            return updated;
          });

          return true; // ✅ éxito
        } else {
          const msg = response?.message || "Error guardando detalle";
          message.error(msg);
          markRowError(record.key, msg);
          return false; // ❌ falla
        }
      } catch (err) {
        console.error(err);
        const msg =
          err?.response?.data?.message || err?.message || "Error guardando detalle";
        message.error(msg);
        markRowError(record.key, msg);
        return false; // ❌ falla
      } finally {
        setLoading(false);
      }
    },
    [oidPo, calcularValorTotal, markRowError, calcularValorTotalConIVA]
  );


  const handleEliminar = useCallback(
    async (record) => {
      try {
        setLoading(true); // opcional: para mostrar indicador
        const resp = await eliminarProductoOrden(
          record.oidInsertado,
          userBodecom.username
        );

        if (resp.status === 200 || resp.status === 204) {
          setDetalles((prev) => {
            const updated = prev.filter((item) => item.key !== record.key);
            calcularValorTotal(updated);
            calcularValorTotalConIVA(updated);
            return updated;
          });
          message.success("Producto eliminado de la orden de compra");
        } else {
          message.error(
            resp.message || "Error eliminando producto de la orden de compra"
          );
        }
      } catch (err) {
        console.error("Error en eliminación:", err);
        message.error("Error eliminando producto de la orden de compra");
      } finally {
        setLoading(false);
      }
    },
    [userBodecom?.username, calcularValorTotal, calcularValorTotalConIVA]
  );

  // Abrir modal pasando el record
  const openSerialModal = useCallback((record) => {
    const oidProducto = getProductoOid(record);
    if (!oidProducto) {
      message.error("ID de producto inválido para asignar seriales.");
      return;
    }
    // Siempre enviamos el alias que el hijo consume: oidInsertado

    const cantidadEfectiva = Number(record.cantidadDB ?? record.cantidad) || 0;
    setSelectedRecord({
      ...record,
      oidInsertado: oidProducto, // el hijo lo usa para el servicio
      cantidad: cantidadEfectiva, // 👈 el hijo tomará esta (persistida)
    });
    setSerialModalVisible(true);
  }, []);

  // Cerrar modal
  const closeSerialModal = useCallback(() => {
    setSerialModalVisible(false);
    setSelectedRecord(null);
  }, []);

  // Guardar seriales
  const handleSaveSerials = (serialesArray) => {
    // Aquí haces lo que necesites: llamar al backend,
    // actualizar el state de tu tabla, etc.
    console.log("Seriales recibidos para", selectedRecord, serialesArray);
    // ejemplo: actualizar el detalle de tu producto...
  };

  // --- Composición final según el toggle ---
  const columns = React.useMemo(() => {
    const baseColumns = [
      {
        title: "#",
        key: "index",
        width: 50,
        align: "center",
        render: (_, __, index) => index + 1,
      },
      {
        title: "* Producto PO",
        dataIndex: "producto",
        align: "center",
        width: 450,
        render: (v, r) => (
          <Input.TextArea
            value={v}
            onChange={(e) => handleChange(e.target.value, r.key, "producto")}
            rows={1}
            style={{
              borderColor: !v ? "red" : undefined,
              width: "100%",
              resize: "vertical",
              overflow: "auto",
            }}
            className="input-derecha"
          />
        ),
      },

      {
        title: "* Fecha de envío",
        dataIndex: "fechaEnvio",
        align: "center",
        width: 200,
        render: (v, r) => {
          // v es un string tipo '2025-09-08' o null

          const value = v ? dayjs(v) : null;

          return (
            <ConfigProvider locale={esES}>
              <DatePicker
                value={value}
                // Muestra al usuario día/mes/año, pero...
                format="DD/MM/YYYY"
                // ... guarda al estado/backend en 'YYYY-MM-DD'
                onChange={(date) => {
                  const iso = date ? date.format("YYYY-MM-DD") : null;
                  handleChange(iso, r.key, "fechaEnvio");
                }}
                placeholder="Selecciona la fecha"
                allowClear
                style={{ width: "100%", borderColor: !v ? "red" : undefined }}
              // opcional: bloquear fechas pasadas
              // disabledDate={(current) => current && current < dayjs().startOf('day')}
              />
            </ConfigProvider>
          );
        },
      },

      {
        title: "* Unidad de medida",
        dataIndex: "unidadmedida",
        key: "unidadmedida",
        align: "center",
        width: 250,
        render: (value, record) => (
          <Select
            showSearch
            placeholder="Seleccionar unidad"
            value={value}
            loading={loadingUnidades}
            onChange={(val) => handleChange(val, record.key, "unidadmedida")}
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }
            style={{
              width: "100%",
              maxWidth: "200px",
              borderColor: !value ? "red" : undefined,
            }}
            status={!value ? "error" : ""}
          >
            {unidades.map((u) => (
              <Option key={u.oid} value={u.oid}>
                {u.Unidad_medida}
              </Option>
            ))}
          </Select>
        ),
      },
      {
        title: "* Cantidad",
        dataIndex: "cantidad",
        align: "center",
        width: 150,
        render: (v, r) => (
          <InputNumber
            min={1}
            max={999999}
            value={v}
            onChange={(val) => handleChange(val, r.key, "cantidad")}
            style={{ borderColor: !v ? "red" : undefined, width: "100%" }}
            className="input-derecha"
          />
        ),
      },
      {
        title: <span style={{ whiteSpace: "nowrap" }}>* Valor Unitario</span>,
        dataIndex: "valor_unitario",
        align: "center",
        width: 250,
        render: (v, r) => (
          <InputNumber
            stringMode
            min={0}
            step={0.01}
            value={v ?? undefined}
            onChange={(val) => handleChange(val, r.key, "valor_unitario")}
            formatter={(value) => {
              if (value == null || value === "") return "";
              const [intPart, decPart = ""] = value.toString().split(".");
              return (
                intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".") +
                (decPart ? "," + decPart.slice(0, 3) : "")
              );
            }}
            parser={(value) => value.replace(/\./g, "").replace(",", ".")}
            style={{
              borderColor: !v ? "red" : undefined,
              width: "100%",
              textAlign: "right",
            }}
            placeholder="0,00"
          />
        ),
      },
      {
        title: "Total sin IVA",
        key: "total",
        align: "right",
        width: 200,
        render: (_, record) => {
          const cantidad = Number(record.cantidad) || 0;
          const valor = Number(record.valor_unitario) || 0;
          return fmtCOP(cantidad * valor);
        },
      },
    ];

    // --- Columnas de detalle (ocultas por defecto; se muestran con el toggle) ---
    const detalleColumns = [
      {
        title: <span style={{ whiteSpace: "nowrap" }}>% IVA</span>,
        dataIndex: "valorIVA",
        align: "center",
        width: 180,
        render: (v, r) => {
          if (!isEditing) {
            if (v === null || v === undefined || v === "")
              return <Tag color="default">No causado</Tag>;
            const n = Number(v);
            return <span>{n === 0 ? "0% (Exento)" : `${n}%`}</span>;
          }
          const valueForSelect =
            v === null || v === undefined || v === ""
              ? "NC"
              : String(Number(v));
          return (
            <Select
              value={valueForSelect}
              onChange={(val) => {
                const parsed = val === "NC" ? null : Number(val);
                handleChange(parsed, r.key, "valorIVA");
              }}
              allowClear
              placeholder="No causado"
              options={IVA_SELECT_OPTIONS}
              style={{ width: "100%" }}
              optionFilterProp="label"
              showSearch
            />
          );
        },
      },
      {
        title: "Valor IVA",
        key: "valor_iva",
        align: "right",
        width: 180,
        render: (_, record) => fmtCOP(valorIVAcalc(record)),
      },
      {
        title: "Total con IVA",
        key: "total_con_iva",
        align: "right",
        width: 200,
        render: (_, record) => fmtCOP(totalConIVA(record)),
      },
    ];

    // --- Columna de acciones ---
    const accionesColumn = {
      title: "",
      key: "acciones",
      align: "center",
      width: 50,
      render: (_, r) => (
        <Space>
          {r.guardado ? (
            <>
              <Tooltip title="Actualizar producto">
                <Button type="link" onClick={() => handleActualizar(r)}>
                  <ReloadOutlined style={{ fontSize: 20, color: "#198754" }} />
                </Button>
              </Tooltip>

              <Popconfirm
                title="¿Quieres asignar seriales al producto?"
                onConfirm={() => openSerialModal(r)}
                okText="Sí"
                cancelText="No"
              >
                <Tooltip
                  title={() => {
                    const total = Number(r.total_seriales) || 0;
                    const qty = Number(r.cantidadDB ?? r.cantidad) || 0;

                    if (total === 0) return `Sin seriales.`;
                    if (total < qty)
                      return `Seriales: ${total} de ${qty} (faltan ${qty - total
                        })`;
                    return `Seriales completos: ${total} de ${qty}`;
                  }}
                >
                  <Space size={8}>
                    {(() => {
                      const total = Number(r.total_seriales) || 0;
                      const qty = Number(r.cantidadDB ?? r.cantidad) || 0;

                      // estado derivado
                      const state =
                        total === 0 ? "none" : total < qty ? "partial" : "ok";

                      // colores por estado
                      const color =
                        state === "partial"
                          ? "#ff4d4f" // rojo
                          : state === "ok"
                            ? "#52c41a" // verde
                            : undefined; // neutro

                      return (
                        <>
                          <Badge
                            count={total}
                            showZero={false} // no muestres “0” si no hay seriales
                            offset={[-2, 6]}
                            // Ant Design v5:
                            styles={
                              color
                                ? { indicator: { backgroundColor: color } }
                                : undefined
                            }
                          // Si usas AntD v4, usa count={<span className="badge-green-count">{total}</span>} y CSS.
                          >
                            <Button
                              type="link"
                              icon={
                                <BarcodeOutlined
                                  style={{ fontSize: 20, color }}
                                />
                              }
                            />
                          </Badge>
                        </>
                      );
                    })()}
                  </Space>
                </Tooltip>
              </Popconfirm>

              <Popconfirm
                title="¿Desea eliminar registro?"
                onConfirm={() => handleEliminar(r)}
                okText="Sí"
                cancelText="No"
              >
                <Tooltip title="Eliminar producto">
                  <Button
                    type="link"
                    danger
                    icon={<DeleteOutlined style={{ fontSize: 20 }} />}
                    title="Eliminar"
                  />
                </Tooltip>
              </Popconfirm>
            </>
          ) : (
            <Tooltip title="Guardar producto">
              <Button type="link" onClick={() => handleGuardar(r)}>
                <SaveOutlined style={{ fontSize: 20 }} />
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    };

    return [
      ...baseColumns,
      ...(showDetalle ? detalleColumns : []),
      accionesColumn,
    ];
  }, [
    showDetalle,
    isEditing,
    loadingUnidades,
    unidades,
    handleChange,
    handleActualizar,
    openSerialModal,
    handleEliminar,
    handleGuardar,
  ]);

  const FormatoMoneda = (valor) =>
    new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 2,
    }).format(valor);



  // 🔹 Detecta los modificados comparando contra snapshot originalData
  const getModificados = () => {
    return detalles.filter(det => {
      const original = originalData.find(o => o.oid === det.oid);
      if (!original) return false;

      const diferente =
        Number(det.cantidad) !== Number(original.cantidad) ||
        Number(det.valor_unitario) !== Number(original.valor_unitario) ||
        String(det.producto) !== String(original.producto) ||
        String(det.unidadmedida) !== String(original.unidadmedida) ||
        Number(det.valorIVA) !== Number(original.valorIVA);

      return diferente;
    });
  };



  const handleActualizarModificados = async () => {
    const modificados = getModificados();
    if (!modificados.length) return;

    setLoading(true);
    try {
      let successCount = 0;

      for (const record of modificados) {
        const ok = await handleActualizar(record);
        if (ok) successCount++;
      }

      // ✅ Refresca snapshot solo si hubo algún éxito
      if (successCount > 0) {
        setOriginalData(detalles);
        message.success(`Se actualizaron ${successCount} producto(s).`);
      } else {
        message.warning("No se pudo actualizar ningún producto.");
      }
    } catch (err) {
      console.error("❌ Error en actualización múltiple:", err);
      message.error("Error al actualizar productos modificados.");
    } finally {
      setLoading(false);
    }
  };






  return (
    <div style={{ marginTop: 16 }}>
      <Spin
        spinning={loading || loadingUnidades}
        tip="Cargando productos..."
        size="large"
        // opcional: para que el spinner cubra mínimo cierto alto
        style={{ width: "100%" }}
      >
        <div className="mb-3">
          <Row justify="space-between" align="middle" className="mb-1">
            <Col>
              <h4>
                Orden de Compra No:{" "}
                <Tag color="blue" style={{ fontSize: 16, padding: "4px 12px" }}>
                  {orden.numero_orden}
                </Tag>
              </h4>
            </Col>
            <Col>
              <Switch
                checked={showDetalle}
                onChange={setShowDetalle}
                checkedChildren="Detalle"
                unCheckedChildren="Detalle"
                style={{ minWidth: 90 }}
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Card bordered hoverable>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h5 className="mb-0">Valor total PO (sin IVA):</h5>
                  <Tag
                    color="processing"
                    style={{ fontSize: 16, padding: "6px 12px" }}
                  >
                    {FormatoMoneda(valorTotalOrden)}
                  </Tag>
                </div>
              </Card>
            </Col>


            <Col xs={24} md={8}>
              <Card bordered hoverable>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h5 className="mb-0">Valor total IVA:</h5>
                  <Tag
                    color="orange"
                    style={{ fontSize: 16, padding: "6px 12px" }}
                  >
                    {FormatoMoneda(valorTotalOrdenConIVA - valorTotalOrden)}
                  </Tag>
                </div>
              </Card>
            </Col>


            <Col xs={24} md={8}>
              <Card bordered hoverable>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <h5 className="mb-0">Valor total PO (con IVA):</h5>
                  <Tag
                    color="green"
                    style={{ fontSize: 16, padding: "6px 12px" }}
                  >
                    {FormatoMoneda(valorTotalOrdenConIVA)}
                  </Tag>
                </div>
              </Card>
            </Col>

          </Row>
        </div>

        <Table
          dataSource={detalles}
          columns={columns}
          rowKey="key"
          pagination={false}
          size="small"
          bordered
          rowClassName={(record) => (record._error ? "row-error" : "")}
        />
        <Button
          type="dashed"
          onClick={handleAdd}
          style={{ marginBottom: 16, marginTop: 16 }}
        >
          + Agregar Producto
        </Button>

        <Button
          type="primary"
          icon={<SaveOutlined />}
          disabled={pendientes.length === 0}
          loading={loading}
          onClick={handleGuardarPendientes}
          style={{ marginLeft: 8, marginBottom: 16, marginTop: 16 }}
        >
          Guardar productos ({pendientes.length})
        </Button>

        {isEditing && (
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            disabled={getModificados().length === 0}
            loading={loading}
            onClick={handleActualizarModificados}
            style={{ marginLeft: 8, marginBottom: 16, marginTop: 16 }}
          >
            Actualizar productos ({getModificados().length})
          </Button>
        )}


        <ProductosPOSeriales
          visible={serialModalVisible}
          record={selectedRecord}
          onClose={closeSerialModal}
          onSave={handleSaveSerials}
        />
      </Spin>
    </div>
  );
};

export default ProductosOrdenServicios;
